import React, { useState, useRef, useEffect } from 'react';

/**
 * 2FA challenge — second step of the login flow.
 *
 * Caller has already POSTed email + password and received a
 * { challenge: '2fa_required', challengeToken } response. This view
 * collects the 6-digit TOTP code (or, optionally, a backup code)
 * and hands it back to the parent via onVerify.
 *
 * Auto-submits on the 6th digit so the user doesn't have to click
 * "Sign in" — same UX Stripe / Google / Slack use for TOTP entry.
 */
export default function TwoFactorChallenge({
  email,
  onVerify,
  onCancel,
  error,
  loading,
}) {
  const [code, setCode] = useState('');
  const [usingBackupCode, setUsingBackupCode] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus the code field on mount so the user can type
    // immediately without clicking.
    if (inputRef.current) inputRef.current.focus();
  }, [usingBackupCode]);

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    onVerify(trimmed);
  };

  const handleCodeChange = (e) => {
    const raw = e.target.value;
    if (usingBackupCode) {
      // Backup codes are 11 chars (XXXXX-XXXXX). Allow alphanumeric +
      // dash, uppercase auto-format, cap length so a paste with
      // surrounding whitespace doesn't break.
      const cleaned = raw.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 11);
      setCode(cleaned);
      return;
    }
    // TOTP: digits only, max 6.
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    // Auto-submit when the user lands on a 6th digit.
    if (digits.length === 6 && !loading) {
      onVerify(digits);
    }
  };

  return (
    <div className="auth-view">
      <div className="hero">
        <h2>Two-factor authentication</h2>
        <p>
          {usingBackupCode
            ? 'Enter one of the backup codes you saved when you set up 2FA.'
            : <>Open your authenticator app and enter the 6-digit code{email && <> for <strong>{email}</strong></>}.</>}
        </p>
      </div>

      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-field">
          <label>{usingBackupCode ? 'Backup code' : '6-digit code'}</label>
          <input
            ref={inputRef}
            type="text"
            inputMode={usingBackupCode ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            placeholder={usingBackupCode ? 'XXXXX-XXXXX' : '123456'}
            value={code}
            onChange={handleCodeChange}
            required
            style={{
              fontSize: usingBackupCode ? 16 : 24,
              letterSpacing: usingBackupCode ? '0.05em' : '0.4em',
              textAlign: 'center',
              fontFamily: usingBackupCode ? 'inherit' : 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontVariantNumeric: 'tabular-nums',
            }}
          />
        </div>

        {error && (
          <div className="error-message">
            <span></span> {error}
          </div>
        )}

        <button type="submit" className="generate-btn ready" disabled={loading || !code.trim()}>
          <span className="btn-text">{loading ? 'Verifying…' : 'Verify and sign in'}</span>
        </button>
      </form>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 }}>
        <button
          type="button"
          className="auth-toggle"
          onClick={() => {
            setUsingBackupCode((v) => !v);
            setCode('');
          }}
          style={{ flex: 1 }}
        >
          {usingBackupCode ? 'Use authenticator code instead' : 'Use a backup code'}
        </button>
        <button type="button" className="auth-toggle" onClick={onCancel} style={{ flex: 1 }}>
          ← Back to sign in
        </button>
      </div>
    </div>
  );
}
